import { Fragment } from 'react';
import { HelpToggletip } from 'ui/tooltip';
import { Popover, PopoverContent, PopoverTrigger, PopoverPlacements } from 'ui/popover';
import { PERCENTAGE, NULL } from 'ui/card/constants';
import styled from 'styled-components';
import { spacing, colors, caption, body2Bold, fontWeights, easing, duration } from 'ui/theme';
import { kebabCase } from 'lodash';
import { pxToRem } from 'utils/convert';
import { GraphBarWithBreakdown } from './GraphBarWithBreakdown';
import { ComparisonBarTooltip } from './ComparisonBarTooltip';
import { formatBarChartNum, sanitizeMediumParam } from '../utils';
import { colorsByMedium, hoverColorsByMedium } from '../constants';
import { InAppDestination } from '../types';

export type GraphBarProps = {
  $width: number;
  $color: string;
  $hoverColor: string;
};

const GraphBar = styled.div<GraphBarProps>`
  width: ${(props) => props.$width}%;
  background: ${(props) => props.$color};
  display: inline-block;
  height: ${spacing.half};
  margin-bottom: ${spacing.half};
  transition: all ${easing.standard} ${duration.short};
  cursor: pointer;
  &:hover {
    background: ${(props) => props.$hoverColor};
    transition: all ${easing.standard} ${duration.short};
  }
`;

const Title = styled.div`
  ${caption};
  color: ${colors.textSecondary};
  height: ${spacing.base};
  line-height: 1rem;
  vertical-align: top;
  display: flex;
  align-items: center;
  gap: ${spacing.quarter};
`;

const Graph = styled.div``;

const KeyValueRow = styled.div`
  margin-top: ${spacing.base};
  margin-bottom: ${spacing.quarter};
  white-space: nowrap;
  display: inline-block;
  display: flex;
  justify-content: space-between;
`;

const GraphContainer = styled.div`
  width: 100%;
  border-radius: ${spacing.quarter};
  overflow: hidden;
`;

const BarWrapper = styled.div`
  height: ${spacing.half};
  background: ${colors.grey10};
`;

const BarVal = styled.div`
  height: ${spacing.base};
  display: inline-block;
  padding-left: ${spacing.quarter};
  vertical-align: top;
  ${body2Bold};
  font-weight: ${fontWeights.bold};
  line-height: 1rem;
  font-feature-settings: 'tnum';
`;

const ComparisonBarWrapper = styled.div`
  border-top: ${spacing.single} solid ${colors.grey15};
  margin-top: ${spacing.base};
  position: relative;
  > div:nth-child(even) {
    width: 100%;
  }
`;

const StyledPopoverContent = styled(PopoverContent)`
  max-width: ${pxToRem(400)};
  background: ${colors.grey80};
  svg {
    fill: ${colors.grey80};
  }
`;

const processRate = (numberType: string, val: number) => {
  return (numberType === PERCENTAGE ? 100 : 1) * val;
};

const calcDenominator = (
  denominator: number,
  values: Record<string, number>,
  metricName: string,
) => {
  if (metricName.toLowerCase().includes('total')) {
    return denominator;
  }
  let maxValue = 1;
  Object.values(values).forEach((val: number) => {
    maxValue = Math.max(maxValue, val);
  });
  return maxValue;
};

export const calcBarWidth = (value: number, denominator: number) =>
  Math.min(100, ((value || 0) / denominator) * 100);

export const ComparisonBarChart = ({
  numberType,
  denominator,
  valuesObj = {},
  destinations = [],
  mediumList = [],
  metricName,
  numCampaignsPerMedium,
  toggletipsPerMedium = {},
}: {
  numberType: string;
  denominator: number;
  valuesObj: Record<string, number>;
  destinations: InAppDestination[];
  mediumList: string[];
  metricName: string;
  numCampaignsPerMedium: Record<string, number>;
  showFilteredProxiedOpensTooltips?: boolean;
  toggletipsPerMedium: Record<string, string>;
}) => {
  const graphDenominator = calcDenominator(denominator, valuesObj, metricName);

  return (
    <>
      {numberType !== NULL && denominator > 0 && (
        <ComparisonBarWrapper data-test={`comparison-bar-chart-${kebabCase(metricName)}`}>
          {mediumList.map((med) => {
            const numberOfCampaigns = numCampaignsPerMedium[sanitizeMediumParam(med)];
            const toggletip = toggletipsPerMedium[med];
            return (
              <Fragment key={med}>
                <KeyValueRow data-test={`key-value-row-${kebabCase(med)}-${kebabCase(metricName)}`}>
                  <Title data-test={`metric-medium-${kebabCase(med)}-${kebabCase(metricName)}`}>
                    {med}
                    {!!toggletip && (
                      <span data-test="tooltip">
                        <HelpToggletip
                          iconProps={{ height: 16, width: 16, color: colors.iconPrimary }}
                          tooltipContent={toggletip}
                        />
                      </span>
                    )}
                  </Title>
                  <BarVal
                    data-test={`metric-medium-value-${kebabCase(med)}-${kebabCase(metricName)}`}
                  >
                    {formatBarChartNum(
                      numberType,
                      valuesObj[med] ? processRate(numberType, valuesObj[med]) : 0,
                    )}
                  </BarVal>
                </KeyValueRow>
                {destinations?.length > 0 && med === 'In-App' ? (
                  <GraphContainer>
                    <Graph>
                      <BarWrapper key={`${med}-${valuesObj[med]}`}>
                        <GraphBarWithBreakdown
                          data-test="graph-bar-w-breakdown"
                          medium={med}
                          numberOfCampaigns={numberOfCampaigns}
                          metricName={metricName}
                          $width={calcBarWidth(valuesObj[med], graphDenominator)}
                          destinations={destinations}
                          numberType={numberType}
                        />
                      </BarWrapper>
                    </Graph>
                  </GraphContainer>
                ) : (
                  <Popover>
                    <StyledPopoverContent side={PopoverPlacements.Top}>
                      <ComparisonBarTooltip
                        medium={med}
                        numberOfCampaigns={numberOfCampaigns}
                        metricName={metricName}
                        value={formatBarChartNum(
                          numberType,
                          valuesObj[med] ? processRate(numberType, valuesObj[med]) : 0,
                        )}
                      />
                    </StyledPopoverContent>
                    <PopoverTrigger asChild>
                      <GraphContainer>
                        <Graph>
                          <BarWrapper key={`${med}-${valuesObj[med]}`}>
                            <GraphBar
                              data-test="graph-bar"
                              $width={calcBarWidth(valuesObj[med], graphDenominator)}
                              $color={colorsByMedium[med]}
                              $hoverColor={hoverColorsByMedium[med]}
                            />
                          </BarWrapper>
                        </Graph>
                      </GraphContainer>
                    </PopoverTrigger>
                  </Popover>
                )}
              </Fragment>
            );
          })}
        </ComparisonBarWrapper>
      )}
    </>
  );
};
